import PasswordFilterDropdown from '@/components/collection_reports_table/password_filter_dropdown'
import DateFilterDropdown, {
  DATE_FILTER_STATIC_RANGES,
  DateFilterLabels,
} from '@/components/doppel_design/date_filter_dropdown/date_filter_dropdown'
import DoppelPrimaryButton from '@/components/doppel_design/doppel_primary_button'
import NumberWidget from '@/components/shared/number_widget'
import { DateOnlyRange, DateRange, DateTimeRange } from '@/components/stats/date_range'
import FilterDropdown, { useSelectedContext } from '@/components/tables/filter_dropdown'
import { getSourceCategoryDisplayValue } from '@/components/web2/spoof_reports_table_filter_bar'
import {
  BooleanFilterOptions,
  ClickTrackingEventTypes,
  DarkWebLeakContents,
  ReportSourceCategory,
  ReportStatus,
  ReportStatusLabels,
  StatsDateBucketInterval,
} from '@/generated/enums'
import {
  useGetAnalyticsSummaryEmailsQuery,
  useGetBrandsQuery,
  useGetTagsQuery,
} from '@/generated/graphql'
import { useClickTracker } from '@/hooks/click_tracker'
import { useIsEmployeeView } from '@/hooks/id_token_claims'
import {
  genReportFilter,
  removeFilterByType,
  upsertFilter,
} from '@/hooks/report_table_filters'
import {
  ReportFilterType,
  StatsTab,
  StatsTabLabels,
  UploaderOption,
} from '@/utils/constants'
import { generatePDF } from '@/utils/download_utils'
import {
  formatSnakeStringWithCapitalization,
  makeDateFilterValue,
} from '@/utils/string_utils'
import {
  DOPPEL_BUTTON_GREY,
  DOPPEL_BUTTON_GREY_SHADE,
  DOPPEL_INTERNAL_PURPLE,
  DOPPEL_WHITE,
  FONT_SIZE_MEDIUM,
  ICON_SIZE,
} from '@/utils/style'
import { ApolloError } from '@apollo/client'
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import axios from 'axios'
import { subDays } from 'date-fns'
import moment from 'moment'
import { useEffect } from 'react'
import {
  IoMdArrowRoundUp,
  IoMdDownload,
  IoMdImages,
  IoMdPin,
  IoMdPricetags,
  IoMdSend,
} from 'react-icons/io'
import { IoPerson } from 'react-icons/io5'
import { formatDateToUTC } from './dashboard_utils'
import DrilldownWidget from './drilldown_widget'
import GroupedTimeSeriesWidget, {
  GroupedTimeSeriesWidgetProps,
} from './grouped_time_series_widget'
// Base interfaces for all dashboard components
export type DashboardProps = {
  tab: string
  orgId: string
  orgName: string
  datePickerRange: DateOnlyRange | null
  setDatePickerRange: (range: DateOnlyRange | null) => void
  filterBy: any
}

export type BaseWidgetProps = {
  error: any
  loading: boolean
  title: string
  helperText: string
  unit?: string
  colSpan?: number
}

export type RankedListWidgetProps = {
  data: any
  rankingMetricDescription?: string
} & BaseWidgetProps

export type TimeSeriesWidgetProps = {
  bucketInterval: StatsDateBucketInterval
  chartData: any
  chartType: 'line' | 'bar'
  legendKeys: string[]
} & BaseWidgetProps

// Common utility functions for date handling
export const getDateRange = (
  datePickerRange: DateOnlyRange | null,
  defaultRange: DateRange,
): DateRange => {
  return datePickerRange || defaultRange
}

export const formatDateRange = (range: DateRange): string => {
  return range.displayLabel
}

export const dateRangeOrDefault = (
  datePickerRange: DateOnlyRange | null,
  defaultRange: DateRange,
): DateRange => {
  return datePickerRange || defaultRange
}

export const startDateOrDefault = (
  datePickerRange: DateOnlyRange | null,
  defaultRange: DateRange,
  includeTime = true,
): string => {
  return formatDateToUTC(
    dateRangeOrDefault(datePickerRange, defaultRange).start,
    includeTime,
  )
}

export const endDateOrDefault = (
  datePickerRange: DateOnlyRange | null,
  defaultRange: DateRange,
  includeTime = true,
): string => {
  return formatDateToUTC(
    dateRangeOrDefault(datePickerRange, defaultRange).end,
    includeTime,
  )
}

const now = new Date()
export const getStaticDateRangeByLabel = (label: DateFilterLabels) => {
  return DATE_FILTER_STATIC_RANGES[label](now).withLabel(label)
}

export const last24HoursRange = new DateTimeRange(subDays(now, 1), now, 'Last 24 Hours')
export const last30DaysRange = getStaticDateRangeByLabel(DateFilterLabels.LAST_30_DAYS)
export const last6MonthsRange = getStaticDateRangeByLabel(
  DateFilterLabels.LAST_6_MONTHS,
)

export const STATS_MIN_DATE = new Date(2024, 8, 1)

export const downloadDashboard = async (
  datePickerRange: DateOnlyRange | null,
  statsDashboard: StatsTab,
  dashboardId: string,
) => {
  const dashboardElement = document.getElementById(dashboardId)

  const pdf = await generatePDF(dashboardElement)
  const dateRange =
    startDateOrDefault(datePickerRange, last30DaysRange, false) +
    '_to_' +
    endDateOrDefault(datePickerRange, last30DaysRange, false)
  pdf.save(`DoppelAnalytics_${StatsTabLabels[statsDashboard]}_${dateRange}.pdf`)
}

export const StatsDashboardHeader = ({
  datePickerRange,
  setDatePickerRange,
  orgName,
  orgId,
  statsDashboard,
  dashboardId,
  onBrandsSelect,
  onSourcesSelect,
  onTagsSelect,
  onUploadTypeSelect,
  onOrganizationEmailLeaksSelect,
  onPasswordComplexitySelect,
  displayDemoData,
  visibleFilters,
  onDarkWebLeakContentsSelect,
}: {
  datePickerRange: DateOnlyRange | null
  setDatePickerRange: (range: DateOnlyRange | null) => void
  orgName: string
  orgId: string
  statsDashboard: StatsTab
  dashboardId: string
  onBrandsSelect?: (brandIds: string[] | null) => void
  onSourcesSelect?: (sources: string[] | null) => void
  onTagsSelect?: (tagIds: string[] | null) => void
  onUploadTypeSelect?: (uploadType: string | null) => void
  onOrganizationEmailLeaksSelect?: (value: string | null) => void
  onPasswordComplexitySelect?: (passwordComplexity: string[] | null) => void
  displayDemoData?: boolean
  visibleFilters?: ReportFilterType[]
  onDarkWebLeakContentsSelect?: (leakContents: string[] | null) => void
}) => {
  const [isEmployeeView] = useIsEmployeeView()
  const [selectedFilters, setSelectedFilters] = useSelectedContext(null)
  const clickTracker = useClickTracker()
  const { data: emails } = useGetAnalyticsSummaryEmailsQuery({
    variables: {
      id: orgId,
    },
  })

  const { data: brandsData } = useGetBrandsQuery({
    variables: {
      where: { organization_id: { _eq: orgId } },
    },
    skip: !orgId,
  })

  const { data: tagsData } = useGetTagsQuery({
    variables: { orgIDs: [orgId], includeGlobal: true },
  })

  const toast = useToast()

  const brandOptions =
    brandsData?.spoofing_entities?.map((brand) => ({
      filterType: ReportFilterType.AnalyticsBrand,
      value: brand.entity_name,
    })) || []

  const sourceOptions =
    Object.values(ReportSourceCategory).map((source) => ({
      filterType: ReportFilterType.AnalyticsSource,
      value: source,
    })) || []

  const tagOptions =
    tagsData?.tags
      ?.filter((tag) => isEmployeeView || !tag.is_internal)
      .map((tag) => ({
        filterType: ReportFilterType.AnalyticsTag,
        value: tag.name,
      })) || []

  const darkWebLeakContentsOptions =
    Object.values(DarkWebLeakContents).map((leakContent) => ({
      filterType: ReportFilterType.AnalyticsDarkWebLeakContents,
      value: leakContent,
    })) || []

  const booleanFilterOptions = Object.values(BooleanFilterOptions).map((option) => ({
    filterType: ReportFilterType.AnalyticsOrganizationEmailLeaks,
    value: option,
  }))

  const handleBrandsSelection = (selected: string[]) => {
    const selectedBrandNames = selected.length > 0 ? selected : null
    if (onBrandsSelect) {
      onBrandsSelect(selectedBrandNames)
    }
  }

  const handleSourcesSelection = (selected: string[]) => {
    const selectedSourceNames = selected.length > 0 ? selected : null
    if (onSourcesSelect) {
      onSourcesSelect(selectedSourceNames)
    }
  }

  const handleTagsSelection = (selected: string[]) => {
    const selectedTagNames = selected.length > 0 ? selected : null
    if (onTagsSelect) {
      onTagsSelect(selectedTagNames)
    }
  }

  const handleUploadTypeSelection = (selected: string[]) => {
    if (onUploadTypeSelect) {
      onUploadTypeSelect(selected.length > 0 ? selected[0] : null)
    }
  }

  const handleDarkWebLeakContentsSelection = (selected: string[]) => {
    const selectedLeakContents = selected.length > 0 ? selected : null
    if (onDarkWebLeakContentsSelect) {
      onDarkWebLeakContentsSelect(selectedLeakContents)
    }
  }

  const handleOrganizationEmailLeaksSelection = (selected: string[]) => {
    const selectedValue = selected.length > 0 ? selected[0] : null
    if (onOrganizationEmailLeaksSelect) {
      onOrganizationEmailLeaksSelect(selectedValue)
    }
  }

  useEffect(() => {
    const selectedReportFilters = selectedFilters?.filter(
      (filter) =>
        filter.filterType === ReportFilterType.AnalyticsBrand ||
        filter.filterType === ReportFilterType.AnalyticsSource ||
        filter.filterType === ReportFilterType.AnalyticsTag ||
        filter.filterType === ReportFilterType.AnalyticsAttribution ||
        filter.filterType === ReportFilterType.AnalyticsDate ||
        filter.filterType === ReportFilterType.AnalyticsDarkWebLeakContents,
    )

    selectedReportFilters?.forEach(({ filterType, value }) => {
      switch (filterType) {
        case ReportFilterType.AnalyticsBrand:
          onBrandsSelect?.(value)
          break

        case ReportFilterType.AnalyticsSource:
          onSourcesSelect?.(value)
          break

        case ReportFilterType.AnalyticsTag:
          onTagsSelect?.(value)
          break

        case ReportFilterType.AnalyticsDarkWebLeakContents:
          onDarkWebLeakContentsSelect?.(value)
          break

        case ReportFilterType.AnalyticsAttribution:
          onUploadTypeSelect?.(value)
          break

        case ReportFilterType.AnalyticsDate: {
          if (!value) {
            setDatePickerRange?.(null)
            break
          }

          const [startDate, endDate] = value
            .split('#')
            .map((dateString) => moment(dateString).toDate())

          setDatePickerRange?.(new DateOnlyRange(startDate, endDate))
          break
        }
      }
    })
  }, [])

  return (
    <Box mb={4} w="100%">
      <Flex alignItems="center" justifyContent="space-between">
        <VStack align="start" spacing={0}>
          <Heading as="h1" size="lg">
            {StatsTabLabels[statsDashboard]}
          </Heading>

          {displayDemoData && (
            <Text>
              The data displayed is for demo purposes only and is not filterable or
              reflective of real-time analytics.
            </Text>
          )}
        </VStack>

        <Flex data-ignore-png="true" gap={3}>
          <Flex alignItems="center" gap={3}>
            {visibleFilters.includes(ReportFilterType.AnalyticsDate) && (
              <DateFilterDropdown
                allowClearSelection
                currentDateRange={datePickerRange}
                dropdownPosition="bottom-start"
                minDate={STATS_MIN_DATE}
                onSelect={(range) => {
                  if (!range) {
                    setDatePickerRange(null)
                    setSelectedFilters(
                      removeFilterByType(
                        ReportFilterType.AnalyticsDate,
                        selectedFilters,
                      ),
                    )
                    return
                  }
                  setDatePickerRange(range)
                  setSelectedFilters(
                    upsertFilter(
                      genReportFilter(
                        ReportFilterType.AnalyticsDate,
                        makeDateFilterValue(range),
                      ),
                      selectedFilters,
                    ),
                  )
                }}
                staticRangesToShow={[
                  DateFilterLabels.LAST_7_DAYS,
                  DateFilterLabels.LAST_30_DAYS,
                  DateFilterLabels.LAST_90_DAYS,
                ]}
              />
            )}

            {visibleFilters.includes(ReportFilterType.AnalyticsBrand) && (
              <FilterDropdown
                alphabetizeFilterOptions={true}
                disabled={!brandOptions?.length}
                dropdownIcon={<IoMdImages size="18" />}
                filterOptions={brandOptions}
                filterType={ReportFilterType.AnalyticsBrand}
                isMultiSelect={true}
                onSelect={handleBrandsSelection}
                showSearchBar={true}
                valueToDisplayFunction={(value) => {
                  const brandOption = brandOptions.find((b) => b.value === value)
                  return brandOption ? (brandOption as any).value : 'Brand'
                }}
              />
            )}

            {visibleFilters.includes(ReportFilterType.AnalyticsSource) && (
              <FilterDropdown
                alphabetizeFilterOptions={true}
                dropdownIcon={<IoMdPin size="18" />}
                filterOptions={sourceOptions}
                filterType={ReportFilterType.AnalyticsSource}
                isMultiSelect={true}
                onSelect={handleSourcesSelection}
                showSearchBar={true}
                valueToDisplayFunction={(value) => {
                  const sourceOption = sourceOptions.find((b) => b.value === value)
                  return sourceOption
                    ? getSourceCategoryDisplayValue(sourceOption.value)
                    : 'Source'
                }}
              />
            )}

            {visibleFilters.includes(ReportFilterType.AnalyticsAttribution) && (
              <FilterDropdown
                alphabetizeFilterOptions={true}
                dropdownIcon={<IoMdArrowRoundUp size="18" />}
                filterOptions={[
                  {
                    filterType: ReportFilterType.AnalyticsAttribution,
                    value: UploaderOption.DOPPEL,
                  },
                  {
                    filterType: ReportFilterType.AnalyticsAttribution,
                    value: UploaderOption.ORGANIZATION,
                  },
                ]}
                filterType={ReportFilterType.AnalyticsAttribution}
                maxDisplayLength={27}
                onSelect={handleUploadTypeSelection}
                valueToDisplayFunction={(value) =>
                  value === UploaderOption.DOPPEL
                    ? 'Doppel'
                    : value === UploaderOption.ORGANIZATION
                    ? orgName
                    : 'Attribution'
                }
                w="auto"
              />
            )}

            {visibleFilters.includes(ReportFilterType.AnalyticsTag) && (
              <FilterDropdown
                alphabetizeFilterOptions={true}
                disabled={!tagOptions?.length}
                dropdownIcon={<IoMdPricetags size="18" />}
                filterOptions={tagOptions}
                filterType={ReportFilterType.AnalyticsTag}
                // isMultiSelect={true} TODO: BE does not support simple tag multi-select
                onSelect={handleTagsSelection}
                showSearchBar={true}
                valueToDisplayFunction={(value) => {
                  const tagOption = tagOptions.find((b) => b.value === value)
                  return tagOption ? (tagOption as any).value : 'Tag'
                }}
              />
            )}

            {visibleFilters.includes(ReportFilterType.AnalyticsDarkWebLeakContents) && (
              <FilterDropdown
                alphabetizeFilterOptions={true}
                disabled={!darkWebLeakContentsOptions?.length}
                dropdownIcon={<IoMdArrowRoundUp size={ICON_SIZE} />}
                filterOptions={darkWebLeakContentsOptions}
                filterType={ReportFilterType.AnalyticsDarkWebLeakContents}
                isMultiSelect
                onSelect={handleDarkWebLeakContentsSelection}
                valueToDisplayFunction={(value) => {
                  const darkWebLeakContentsOption = darkWebLeakContentsOptions.find(
                    (b) => b.value === value,
                  )
                  return darkWebLeakContentsOption
                    ? formatSnakeStringWithCapitalization(
                        darkWebLeakContentsOption.value,
                      )
                    : 'Leak Contents'
                }}
              />
            )}

            {visibleFilters.includes(
              ReportFilterType.AnalyticsOrganizationEmailLeaks,
            ) && (
              <FilterDropdown
                dropdownIcon={<IoPerson size="15" />}
                filterOptions={booleanFilterOptions}
                filterType={ReportFilterType.OrganizationEmailLeaks}
                onSelect={handleOrganizationEmailLeaksSelection}
                valueToDisplayFunction={(value) =>
                  value === BooleanFilterOptions.TRUE
                    ? 'Organization Email Leaks'
                    : 'All Leaks'
                }
              />
            )}

            {visibleFilters.includes(ReportFilterType.PasswordComplexity) && (
              <PasswordFilterDropdown
                margin={0}
                onClear={() => onPasswordComplexitySelect?.(null)}
                onSelect={onPasswordComplexitySelect}
              />
            )}
          </Flex>

          <Flex alignItems="center" data-ignore-png="true" gap={3}>
            {isEmployeeView && (
              <DoppelPrimaryButton
                bgColor={DOPPEL_INTERNAL_PURPLE}
                fontSize={FONT_SIZE_MEDIUM}
                leftIcon={<IoMdSend size="18" />}
                onClick={() => {
                  clickTracker(ClickTrackingEventTypes.ANALYTICS_SEND_TO_CUSTOMERS, {
                    dashboard_name: StatsTabLabels[statsDashboard],
                  })
                  sendDashboard(
                    datePickerRange,
                    statsDashboard,
                    orgName,
                    emails,
                    toast,
                    dashboardId,
                  )
                }}
              >
                Send to Customers
              </DoppelPrimaryButton>
            )}

            <Tooltip label="Download">
              <IconButton
                _hover={{ bgColor: DOPPEL_BUTTON_GREY_SHADE }}
                aria-label="Download Dashboard"
                bgColor={DOPPEL_BUTTON_GREY}
                color={DOPPEL_WHITE}
                fontSize={FONT_SIZE_MEDIUM}
                height={8}
                icon={<IoMdDownload size="18" />}
                onClick={() =>
                  downloadDashboard(datePickerRange, statsDashboard, dashboardId)
                }
              />
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  )
}

type StatErrorLoading = {
  stat: any
  // used for drilldown widget, alerts to load when user clicks on number widget
  data?: any
  error: ApolloError | undefined
  loading: boolean
}

type NumberWidgetsProps = {
  alertsSurfacedTotal?: StatErrorLoading
  alertsVerifiedTotal?: StatErrorLoading
  alertsResolvedTotal?: StatErrorLoading
  medianResolutionTime?: StatErrorLoading
  medianMitigationTime?: StatErrorLoading
  orgName: string
  datePickerRange: DateOnlyRange | null
  colSpan?: number
  showInMinutes?: boolean
  isDrilldownWidget?: boolean
}

export const StatsDashboardNumberWidgets = ({
  alertsSurfacedTotal,
  alertsVerifiedTotal,
  alertsResolvedTotal,
  medianResolutionTime,
  medianMitigationTime,
  orgName,
  datePickerRange,
  colSpan = 6,
  showInMinutes = false,
}: NumberWidgetsProps) => {
  return (
    <>
      {alertsSurfacedTotal && (
        <NumberWidget
          clickTrackingEventName={
            ClickTrackingEventTypes.ANALYTICS_ALERTS_CREATED_NUMBER_WIDGET_DRILLDOWN
          }
          colSpan={colSpan}
          drilldownData={alertsSurfacedTotal.data}
          error={alertsSurfacedTotal.error}
          helperText={dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel}
          infoContent={`Number of alerts created`}
          loading={alertsSurfacedTotal.loading}
          title="Alerts Created"
          value={alertsSurfacedTotal.stat}
        />
      )}

      {alertsVerifiedTotal && (
        <NumberWidget
          colSpan={colSpan}
          drilldownData={alertsVerifiedTotal.data}
          error={alertsVerifiedTotal.error}
          helperText={dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel}
          infoContent={`Number of alerts moved to ${formatSnakeStringWithCapitalization(
            ReportStatusLabels[ReportStatus.REPORTED],
          )}, ${formatSnakeStringWithCapitalization(
            ReportStatusLabels[ReportStatus.RESOLVED],
          )}, ${formatSnakeStringWithCapitalization(
            ReportStatusLabels[ReportStatus.NO_ACTION],
          )}, or ${formatSnakeStringWithCapitalization(
            ReportStatusLabels[ReportStatus.NEEDS_ACTION],
          )}`}
          loading={alertsVerifiedTotal.loading}
          title="Alerts Verified"
          value={alertsVerifiedTotal.stat}
        />
      )}

      {alertsResolvedTotal && (
        <NumberWidget
          clickTrackingEventName={
            ClickTrackingEventTypes.ANALYTICS_ALERTS_RESOLVED_NUMBER_WIDGET_DRILLDOWN
          }
          colSpan={colSpan}
          drilldownData={alertsResolvedTotal.data}
          error={alertsResolvedTotal.error}
          helperText={dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel}
          infoContent="Number of alerts fully taken down"
          loading={alertsResolvedTotal.loading}
          title="Alerts Resolved"
          value={alertsResolvedTotal.stat}
        />
      )}

      {medianResolutionTime && (
        <NumberWidget
          colSpan={colSpan}
          error={medianResolutionTime.error}
          helperText={dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel}
          infoContent="Median time taken from alert creation to full takedown"
          loading={medianResolutionTime.loading}
          title="Median Resolution Time"
          value={medianResolutionTime.stat}
        />
      )}

      {medianMitigationTime && (
        <NumberWidget
          colSpan={colSpan}
          error={medianMitigationTime.error}
          helperText={dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel}
          infoContent="Median time taken from alert creation to first enforcement approval"
          loading={medianMitigationTime.loading}
          title="Median Mitigation Time"
          value={medianMitigationTime.stat}
        />
      )}
    </>
  )
}

type AlertsCreatedWidgetProps = {
  chartData: any
  error: ApolloError | undefined
  legendKeys: string[]
  loading: boolean
  isDrilldownWidget?: boolean
  datePickerRange: DateOnlyRange | null
  colSpan?: number
  title?: string
}

export const AlertsCreatedWidget = ({
  chartData,
  error,
  legendKeys,
  loading,
  datePickerRange,
  colSpan,
  title = 'Alerts Created (by brand)',
  isDrilldownWidget = false,
}: AlertsCreatedWidgetProps) => {
  const widgetProps = {
    bucketInterval: StatsDateBucketInterval.DAILY,
    chartData: chartData,
    chartType: 'bar',
    colSpan: colSpan,
    error: error,
    helperText: dateRangeOrDefault(datePickerRange, last30DaysRange).displayLabel,
    legendKeys: legendKeys,
    loading: loading,
    title: title,
    isDrilldownWidget: isDrilldownWidget,
    clickTrackingEventName:
      ClickTrackingEventTypes.ANALYTICS_ALERTS_CREATED_GRAPH_DRILLDOWN,
  } as GroupedTimeSeriesWidgetProps<string>

  const chart = (
    <GroupedTimeSeriesWidget
      bucketInterval={widgetProps.bucketInterval}
      chartData={widgetProps.chartData}
      chartType={widgetProps.chartType}
      colSpan={widgetProps.colSpan}
      error={widgetProps.error}
      helperText={widgetProps.helperText}
      legendKeys={widgetProps.legendKeys}
      loading={widgetProps.loading}
      title={widgetProps.title}
    />
  )

  if (isDrilldownWidget) {
    return <DrilldownWidget widgetProps={widgetProps}>{chart}</DrilldownWidget>
  } else {
    return chart
  }
}

type MedianResolutionWidgetProps = {
  chartData: any
  error: ApolloError | undefined
  legendKeys: string[]
  loading: boolean
  datePickerRange: DateOnlyRange | null
  colSpan?: number
  showInMinutes?: boolean
  bucketInterval?: StatsDateBucketInterval
}

export const MedianResolutionWidget = ({
  chartData,
  error,
  legendKeys,
  loading,
  datePickerRange,
  colSpan,
  showInMinutes = false,
  bucketInterval = StatsDateBucketInterval.MONTHLY,
}: MedianResolutionWidgetProps) => {
  const processedData = showInMinutes
    ? chartData.map((item: any) => ({
        ...item,
        values: {
          ...item.values,
          suspicious_emails: item.values.suspicious_emails * 60,
        },
      }))
    : chartData

  const title = showInMinutes
    ? 'Median Resolution Time (in minutes)'
    : 'Median Resolution Time (in hours)'

  return (
    <GroupedTimeSeriesWidget
      bucketInterval={bucketInterval}
      chartData={processedData}
      chartType="line"
      colSpan={colSpan}
      error={error}
      helperText={dateRangeOrDefault(datePickerRange, last6MonthsRange).displayLabel}
      legendKeys={legendKeys}
      loading={loading}
      title={title}
    />
  )
}

export const sendDashboard = async (
  datePickerRange: DateOnlyRange | null,
  statsDashboard: StatsTab,
  orgName: string,
  emails: any,
  toast: any,
  dashboardId: string,
) => {
  const dashboardElement = document.getElementById(dashboardId)
  const pdf = await generatePDF(dashboardElement)

  const pdfBlob = pdf.output('blob')

  // Prepare the payload
  const formData = new FormData()
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
      accept: 'application/json',
    },
  }

  const dateRange =
    startDateOrDefault(datePickerRange, last30DaysRange, false) +
    '_to_' +
    endDateOrDefault(datePickerRange, last30DaysRange, false)

  const emailDateRange =
    startDateOrDefault(datePickerRange, last30DaysRange, false) +
    ' to ' +
    endDateOrDefault(datePickerRange, last30DaysRange, false)

  formData.append('file', pdfBlob)
  formData.append(
    'filename',
    `DoppelAnalytics_${StatsTabLabels[statsDashboard]}_${dateRange}.pdf`,
  )
  formData.append(
    'emails',
    JSON.stringify(emails?.organizations[0].analytics_summary_recipient_emails),
  )
  formData.append('organization_name', orgName)
  formData.append('email_date_range', emailDateRange)

  await axios
    .post(
      'https://us-west1-nft-index.cloudfunctions.net/send-analytics-summary',
      formData,
      config,
    )
    .then((response) => {
      toast({
        title: 'Success!',
        description: `Summary has been sent to customer.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    })
    .catch(() => {
      toast({
        title: 'Error',
        description: `Summary was not sent to customer.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    })
}
